/**
 * How a consumer reaches the API: from the provider, never by name (wf-12 U-09, step 3).
 *
 * The hook THROWS when there is no provider above it. That is the same rule as U-12 in a
 * different place: the failure mode this console cannot afford is a screen that renders as
 * though it asked and got nothing. A missing provider is a wiring mistake, it is the same on
 * every render, and it should be impossible to ship past — a `null` here would be checked
 * once, defaulted to an empty list somewhere, and become a board that reads "no deployments"
 * when the truth is "nobody connected the API".
 *
 * The context and the hook live in a `.ts` file with no component in it, so the provider
 * component can be the only export of its own `.tsx` and Fast Refresh stays intact.
 */
import { createContext, useContext } from 'react'
import type { FleetApi } from './fleet-api.ts'

export const FleetApiContext = createContext<FleetApi | null>(null)

/**
 * The API if there is one, or null, WITHOUT throwing. For chrome and an explicit connecting
 * state only.
 *
 * The throw above is right for a screen and wrong for the shell around it, and the difference
 * is what each one would render. A screen with no API has nothing to say and must not pretend
 * it asked; the chrome has a rail, a breadcrumb and a search box that are correct with no API
 * at all, and it is legitimately mounted that way -- `tests/unit/chrome.test.tsx` renders it
 * bare, and so does anyone checking the design system.
 *
 * This does NOT reintroduce the failure the throw prevents. The rule is that a null here must
 * surface as "connecting" or "not connected", never as "nothing to report": chrome says it is
 * not connected, while the user-management and audit screens render a named loading panel until
 * the root provider resolves. A caller that defaults this to an empty list has reintroduced
 * exactly the bug, and should use `useFleetApi()` instead.
 */
export function useOptionalFleetApi(): FleetApi | null {
  return useContext(FleetApiContext)
}

export function useFleetApi(): FleetApi {
  const api = useContext(FleetApiContext)
  if (!api) {
    throw new Error(
      'useFleetApi() was called outside <FleetApiProvider>. The console reaches the fleet ' +
        'through the provider so that no screen names an implementation (wf-12 U-09); wrap ' +
        'the tree in <FleetApiProvider api={await createFleetApi()}>.',
    )
  }
  return api
}
